// function to handle breaking out of header blocks, lists, etc
// most of this code is lifted from https://github.com/icelab/draft-js-block-breakout-plugin
import { CharacterMetadata, ContentBlock, EditorState, genKey, Modifier, RichUtils, SelectionState } from "draft-js";
import { List, Map, Repeat } from "immutable";

// call this from handleReturn. Takes editorState, returns a new editorState
// or false if the editor should keep the default behavior
export const blockBreakout = (editorState) => {
  const currentBlockType = RichUtils.getCurrentBlockType(editorState);
  const splitBreakoutTypes = [
    "header-one",
    "header-two",
    "header-three",
    "header-four",
    "header-five",
    "header-six",
    "blockquote",
  ];
  if (splitBreakoutTypes.includes(currentBlockType)) {
    return splitBreakout(editorState);
  }

  const inlineBreakoutTypes = ["unordered-list-item", "ordered-list-item"];
  if (inlineBreakoutTypes.includes(currentBlockType)) {
    return inlineBreakout(editorState);
  }

  return false;
};

export const selectedBlocks = (contentState, selection) => {
  const blockMap = contentState.getBlockMap();
  const keySeq = blockMap.keySeq();
  const start = keySeq.indexOf(selection.getStartKey());
  const end = keySeq.indexOf(selection.getEndKey());
  return blockMap.slice(start, end + 1);
};

// break out of a block type (i.e. list) when the user presses return while in
// an empty block
export const inlineBreakout = (editorState) => {
  const selectionState = editorState.getSelection();
  // only operate on blocks where the user has nothing selected
  if (!selectionState.isCollapsed()) {
    return false;
  }
  const contentState = editorState.getCurrentContent();
  const currentBlock = contentState.getBlockForKey(selectionState.getEndKey());
  // only operate on empty blocks
  if (!(currentBlock.getText().length === 0)) {
    return false;
  }
  const asUnstyled = Modifier.setBlockType(contentState, selectionState, "unstyled");
  return EditorState.push(editorState, asUnstyled, "change-block-type");
};

// break out of a block type (i.e. heading, blockquote) by inserting an unstyled
// block before or after (based on selection location)
export const splitBreakout = (editorState) => {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();

  const afterRemoval = Modifier.removeRange(contentState, selectionState, "backward");
  const targetSelection = afterRemoval.getSelectionAfter();
  const afterSplit = Modifier.splitBlock(afterRemoval, targetSelection);
  const insertionTarget =
    selectionState.getStartOffset() === 0
      ? SelectionState.createEmpty(selectionState.getStartKey())
      : afterSplit.getSelectionAfter();
  const asUnstyled = Modifier.setBlockType(afterSplit, insertionTarget, "unstyled");
  return EditorState.push(editorState, asUnstyled, "split-block");
};

// reimplement atomicBlockUtils' version but using block metadata API
export const insertAtomicBlock = (editorState, data = {}) => {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  const currentBlock = contentState.getBlockForKey(selectionState.getEndKey());
  const newBlock = new ContentBlock({
    key: genKey(),
    type: "atomic",
    text: "",
    characterList: List(),
    data: Map(data),
  });
  const isLast = isLastBlock(contentState.blockMap, currentBlock);
  // empty block, collapsed selection means replace block
  if (selectionState.isCollapsed() && currentBlock.getType() !== "atomic" && currentBlock.getText().length === 0) {
    if (isLast) {
      // special case: if it's the final empty block in the report, don't overwrite
      const newContentState = contentState
        .set("blockMap", insertBlockBefore(contentState.blockMap, currentBlock.getKey(), newBlock))
        .set("selectionAfter", SelectionState.createEmpty(newBlock.getKey()).set("hasFocus", true));
      return EditorState.push(editorState, newContentState, "insert-fragment");
    }
    const cloneBlock = newBlock.set("key", currentBlock.getKey());
    const newContentState = contentState
      .set("blockMap", contentState.blockMap.set(currentBlock.getKey(), cloneBlock))
      .set("selectionAfter", SelectionState.createEmpty(cloneBlock.getKey()).set("hasFocus", true));
    return EditorState.push(editorState, newContentState, "change-block-type");
  } else {
    // non-empty or non-text, insert new block after
    const withAtomic = insertBlockAfter(contentState.blockMap, currentBlock.getKey(), newBlock);
    const empty = emptyBlock();
    const withEmpty = insertBlockAfter(withAtomic, newBlock.getKey(), empty);
    // if this is the last block in the report, make sure that we're padding with an empty text block
    const newContentState = contentState.merge({
      blockMap: isLast ? withEmpty : withAtomic,
      selectionBefore: editorState.getSelection(),
      selectionAfter: SelectionState.createEmpty(newBlock.getKey()).set("hasFocus", true),
    });
    return EditorState.push(editorState, newContentState, "insert-fragment");
  }
};

export const replaceWithEmpty = (editorState, blockKey) => {
  const contentState = editorState.getCurrentContent();
  const empty = emptyBlock();
  const newBlockMap = replaceBlock(contentState.getBlockMap(), blockKey, empty);
  const selectionAfter = SelectionState.createEmpty(empty.getKey()).set("hasFocus", true);
  const newEditorState = EditorState.push(
    editorState,
    contentState.merge({ blockMap: newBlockMap, selectionAfter }),
    "change-block-type"
  );
  return newEditorState;
};

export const removeBlock = (contentState, blockKey) => {
  const blockBefore = contentState.getBlockBefore(blockKey);
  const blockAfter = contentState.getBlockAfter(blockKey);
  if (!blockBefore) {
    // removeRange is poorly behaved for the first block in the report, so
    // we'll handle this case ourselves
    const newBlockMap = contentState.getBlockMap().delete(blockKey);
    const newSelection = SelectionState.createEmpty(blockAfter.getKey()).set(
      "hasFocus",
      contentState.getSelectionAfter().getHasFocus()
    );
    return contentState.merge({
      blockMap: newBlockMap,
      selectionAfter: newSelection,
    });
  }

  const selectionToRemove = SelectionState.createEmpty(blockKey).merge({
    anchorKey: blockBefore.getKey(),
    anchorOffset: blockBefore.getText().length,
    focusOffset: contentState.getBlockForKey(blockKey).getText().length,
    isBackward: false,
  });

  return Modifier.removeRange(contentState, selectionToRemove, "backward");
};

export const convertBlockToHeader = (editorState) => {
  const selection = editorState.getSelection();
  // for some unknown reason, the currentContent selectionAfter is set to the
  // preceeding block here. Maybe due to a stray click event? Anyways, make sure
  // that we don't overwrite the editorState selection state as part of the
  // reformat operation
  const content = editorState.getCurrentContent().set("selectionAfter", selection);
  const block = content.getBlockMap().get(selection.getStartKey());
  // zero out inline styles for the block by replacing the characterList
  const characterList = List(Repeat(CharacterMetadata.create(), block.getText().length));
  const newBlock = block.merge({ type: "header-two", characterList });
  const newContent = content.setIn(["blockMap", selection.getStartKey()], newBlock);
  const newEditorState = EditorState.push(editorState, newContent, "change-block-type");
  return newEditorState;
};

export const emptyBlock = () =>
  new ContentBlock({
    key: genKey(),
    type: "unstyled",
    text: "",
    characterList: List(),
  });

export const isLastBlock = (blockMap, block) => {
  const { tails } = partition(blockMap, block);
  return tails.size === 0;
};

export const partition = (blockMap, pivot) => {
  const heads = blockMap.takeUntil((b) => b === pivot);
  const tails = blockMap.skipUntil((b) => b === pivot).rest();
  return { heads, tails, pivot };
};

export const insertBlockBefore = (blockMap, blockKey, newBlock) => {
  const pivot = blockMap.find((b) => b.getKey() === blockKey);
  const { heads, tails } = partition(blockMap, pivot);
  return heads.set(newBlock.getKey(), newBlock).set(pivot.getKey(), pivot).concat(tails);
};

export const insertBlockAfter = (blockMap, blockKey, newBlock) => {
  const pivot = blockMap.find((b) => b.getKey() === blockKey);
  const { heads, tails } = partition(blockMap, pivot);
  return heads.set(pivot.getKey(), pivot).set(newBlock.getKey(), newBlock).concat(tails);
};

export const replaceBlock = (blockMap, blockKey, newBlock) => {
  const pivot = blockMap.find((b) => b.getKey() === blockKey);
  const { heads, tails } = partition(blockMap, pivot);
  return heads.set(newBlock.getKey(), newBlock).concat(tails);
};
